// Homepage sections — video hero + product lineup. Config-driven:
// each component reads a `cfg` object (injected per-section from Shopify settings)
// and falls back to the original design content when a value is blank.
function useIsMobile(bp = 768) {
  const [m, setM] = React.useState(() => window.matchMedia(`(max-width:${bp}px)`).matches);
  React.useEffect(() => {
    const q = window.matchMedia(`(max-width:${bp}px)`);
    const f = e => setM(e.matches);
    q.addEventListener('change', f);
    return () => q.removeEventListener('change', f);
  }, [bp]);
  return m;
}
// text → {__html} with newlines as <br> (lets client add line breaks in a textarea)
function H(s) {
  return {
    __html: (s == null ? '' : String(s)).replace(/\r?\n/g, '<br>')
  };
}
function P(v, d) {
  return v === undefined || v === null || v === '' ? d : v;
}
window.CravyHome = window.CravyHome || {};
function cravyAddToCart(variantId) {
  if (!variantId) {
    location.href = '/collections/all';
    return;
  }
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: variantId,
      quantity: 1
    })
  }).then(r => r.json()).then(() => {
    location.href = '/cart';
  }).catch(() => {
    location.href = '/cart';
  });
}

// ---- MARQUEE wrapper (maps section settings onto the shared Marquee) ----
function HomeMarquee({
  cfg
}) {
  const c = cfg || {};
  return /*#__PURE__*/React.createElement(Marquee, {
    text: P(c.text, 'Crispy · Golden · Cravy'),
    speed: Number(P(c.speed, 34)),
    bg: P(c.bg, 'var(--cravy-red)'),
    color: P(c.color, '#fff')
  });
}
window.CravyHome.marquee = HomeMarquee;

// ---- HERO: full-bleed video, scroll scrubs playback, overlay text fades out ----
function VideoHero({
  cfg
}) {
  const c = cfg || {};
  // Transparent (alpha) hero video: VP9-alpha WebM for Chrome/FF/Edge/Android,
  // HEVC-alpha MOV for Safari/iOS. The solid section background shows through.
  const canWebmAlpha = typeof document !== 'undefined' && document.createElement('video').canPlayType('video/webm; codecs="vp9"');
  const VIDEO_SRC = canWebmAlpha ? P(c.video_webm, P(c.video, '')) : P(c.video_mov, P(c.video, ''));
  // Shopify's CDN mislabels .webm assets as audio/webm — force the correct video MIME so the <video> renders.
  const VIDEO_TYPE = canWebmAlpha ? 'video/webm' : 'video/quicktime';
  const [wrapRef, p] = useScrollProgress();
  const isMobile = useIsMobile();
  const videoRef = React.useRef(null);
  const [dur, setDur] = React.useState(0);
  const targetRef = React.useRef(0);
  const seqCanvasRef = React.useRef(null);
  const seqImgRef = React.useRef(null);
  const [seqReady, setSeqReady] = React.useState(0);
  const SEQ_N = 40,
    SEQ_COLS = 8,
    SEQ_ROWS = 5;
  const b1 = P(c.btn1_link, '#products'),
    b2 = P(c.btn2_link, '/pages/about');
  // ---- load the video ----
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v || !VIDEO_SRC) return;
    let url;
    // Mobile plays the .mov directly (correct MIME, best iOS alpha support); desktop
    // blob-wraps so scrubbing is smooth and Shopify's audio/webm mislabel is fixed.
    if (isMobile || VIDEO_SRC.startsWith('blob:')) {
      v.src = VIDEO_SRC;
      v.load();
    } else {
      fetch(VIDEO_SRC).then(r => r.blob()).then(b => {
        url = URL.createObjectURL(new Blob([b], {
          type: VIDEO_TYPE
        }));
        v.src = url;
        v.load();
      }).catch(() => {
        v.src = VIDEO_SRC;
      });
    }
    const onMeta = () => setDur(v.duration || 0);
    v.addEventListener('loadedmetadata', onMeta);
    if (v.readyState >= 1) onMeta();
    return () => {
      v.removeEventListener('loadedmetadata', onMeta);
      if (url) URL.revokeObjectURL(url);
    };
  }, [VIDEO_SRC, isMobile]);
  // ---- mobile: autoplay + loop (iOS only composites the alpha during playback) ----
  React.useEffect(() => {
    if (!isMobile) return;
    const v = videoRef.current;
    if (!v) return;
    v.loop = true;
    const play = () => {
      const pr = v.play();
      if (pr && pr.catch) pr.catch(() => {});
    };
    v.addEventListener('loadeddata', play);
    v.addEventListener('canplay', play);
    play();
    return () => {
      v.removeEventListener('loadeddata', play);
      v.removeEventListener('canplay', play);
    };
  }, [isMobile, VIDEO_SRC]);
  // ---- desktop: scroll scrubs playback ----
  React.useEffect(() => {
    if (isMobile) return;
    const vp = Math.min(1, Math.max(0, (p - 0.12) / 0.88));
    targetRef.current = vp * dur;
  }, [p, dur, isMobile]);
  React.useEffect(() => {
    if (isMobile || !dur) return;
    let raf;
    const loop = () => {
      const v = videoRef.current;
      if (v && !v.seeking) {
        const cc = v.currentTime,
          t2 = targetRef.current;
        const d = t2 - cc;
        if (Math.abs(d) > 0.02) v.currentTime = Math.abs(d) > 1.2 ? t2 : cc + d * 0.28;
      }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => cancelAnimationFrame(raf);
  }, [dur, isMobile]);

  // ---- mobile: scroll scrubs a transparent sprite-sheet animation onto a canvas (iOS-safe alpha) ----
  React.useEffect(() => {
    if (!isMobile) return;
    const url = P(c.mobile_seq, '');
    if (!url) return;
    const sp = new Image();
    sp.onload = () => {
      seqImgRef.current = sp;
      setSeqReady(x => x + 1);
    };
    sp.src = url;
  }, [isMobile, c.mobile_seq]);
  React.useEffect(() => {
    if (!isMobile) return;
    const cv = seqCanvasRef.current,
      sp = seqImgRef.current;
    if (!cv || !sp || !sp.complete || !sp.naturalWidth) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = cv.getBoundingClientRect();
    const W = Math.round(rect.width * dpr),
      Hh = Math.round(rect.height * dpr);
    if (!W || !Hh) return;
    if (cv.width !== W || cv.height !== Hh) {
      cv.width = W;
      cv.height = Hh;
    }
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, W, Hh);
    const fw = sp.naturalWidth / SEQ_COLS,
      fh = sp.naturalHeight / SEQ_ROWS;
    const idx = Math.min(SEQ_N - 1, Math.max(0, Math.round(p * (SEQ_N - 1))));
    const col = idx % SEQ_COLS,
      row = Math.floor(idx / SEQ_COLS);
    // CONTAIN: fit the whole frame with no crop; center horizontally, anchor to the
    // bottom edge (frames are bottom-aligned, so the bag stays flush to the bottom).
    const scale = Math.min(W / fw, Hh / fh);
    const dw = fw * scale,
      dh = fh * scale;
    ctx.drawImage(sp, col * fw, row * fh, fw, fh, (W - dw) / 2, Hh - dh, dw, dh);
  }, [p, isMobile, seqReady]);

  // ---- MOBILE: text on top, scroll-scrubbed transparent animation in the lower half ----
  if (isMobile) {
    const mTextO = Math.max(0, 1 - p / 0.22); // text + CTAs fade out as the animation plays on scroll
    return /*#__PURE__*/React.createElement("section", {
      ref: wrapRef,
      "data-screen-label": "Video hero",
      style: {
        height: '200vh',
        position: 'relative',
        background: P(c.bg, '#dd331d')
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        position: 'sticky',
        top: 0,
        height: '100dvh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        position: 'relative',
        zIndex: 2,
        padding: 'clamp(70px,10vh,108px) 20px 4px',
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        opacity: mTextO,
        transform: `translateY(${((1 - mTextO) * -14).toFixed(1)}px)`,
        pointerEvents: mTextO < 0.05 ? 'none' : 'auto'
      }
    }, /*#__PURE__*/React.createElement("h1", {
      className: "fadein-1",
      style: {
        color: P(c.text, '#fff'),
        textShadow: '0 2px 20px rgba(0,0,0,0.35)'
      },
      dangerouslySetInnerHTML: H(P(c.heading, 'More than fries.\nMore than food.'))
    }), /*#__PURE__*/React.createElement("p", {
      className: "fadein-2",
      style: {
        margin: 0,
        fontFamily: 'var(--font-body)',
        fontWeight: 400,
        fontSize: 15,
        lineHeight: 1.38,
        color: P(c.text, '#fff'),
        textShadow: '0 1px 12px rgba(0,0,0,0.4)'
      },
      dangerouslySetInnerHTML: H(P(c.body, 'Restaurant-grade fries made for the moments that matter. Whether it’s a movie night, family dinner, game day, or unexpected guests, Cravy brings restaurant-quality fries to your table—so you can focus on what really matters: being together.'))
    }), /*#__PURE__*/React.createElement("div", {
      className: "fadein-3",
      style: {
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        marginTop: 2
      }
    }, /*#__PURE__*/React.createElement("a", {
      className: "herobtn herobtn-dark",
      href: b1,
      style: {
        textDecoration: 'none',
        fontFamily: 'var(--font-body)',
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '13px 26px',
        background: P(c.btn1_bg, 'var(--cravy-yellow)'),
        color: P(c.btn1_color, '#000'),
        borderRadius: 999
      }
    }, P(c.btn1_text, 'Shop fries')), /*#__PURE__*/React.createElement("a", {
      className: "herobtn herobtn-lite",
      href: b2,
      style: {
        textDecoration: 'none',
        fontFamily: 'var(--font-body)',
        fontWeight: 700,
        fontSize: 15,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        padding: '13px 26px',
        background: P(c.btn2_bg, 'rgba(255,255,255,0.95)'),
        color: P(c.btn2_color, '#000'),
        borderRadius: 999
      }
    }, P(c.btn2_text, 'Discover our story')))), /*#__PURE__*/React.createElement("div", {
      style: {
        position: 'relative',
        flex: '1 1 auto',
        minHeight: '44dvh',
        overflow: 'hidden',
        zIndex: 1
      }
    }, /*#__PURE__*/React.createElement("canvas", {
      ref: seqCanvasRef,
      style: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%'
      }
    }))));
  }

  // ---- DESKTOP: sticky hero, scroll scrubs the video ----
  const textO = Math.max(0, 1 - p / 0.16);
  const heroStyles = {
    stacked: {
      top: '50%',
      left: 'clamp(24px,5vw,80px)',
      right: 'auto',
      transform: 'translateY(-50%)',
      textAlign: 'left',
      alignItems: 'flex-start'
    },
    lower: {
      bottom: 'clamp(40px,7vh,80px)',
      left: 'clamp(24px,5vw,80px)',
      right: 'auto',
      textAlign: 'left',
      alignItems: 'flex-start'
    },
    corner: {
      top: 'clamp(48px,10vh,110px)',
      left: 'clamp(24px,5vw,80px)',
      textAlign: 'left',
      alignItems: 'flex-start'
    }
  };
  const pos = heroStyles[P(c.layout, 'stacked')] || heroStyles.stacked;
  return /*#__PURE__*/React.createElement("section", {
    ref: wrapRef,
    "data-screen-label": "Video hero",
    style: {
      height: '420vh',
      position: 'relative',
      background: P(c.bg, '#dd331d')
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'sticky',
      top: 0,
      height: '100vh',
      overflow: 'hidden'
    }
  }, /*#__PURE__*/React.createElement("video", {
    ref: videoRef,
    muted: true,
    playsInline: true,
    preload: "auto",
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transform: `translateX(${(20 * Math.max(0, 1 - p / 0.25)).toFixed(2)}%)`,
      willChange: 'transform'
    }
  }), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      gap: 'clamp(10px,1.6vh,20px)',
      opacity: textO,
      pointerEvents: textO < 0.05 ? 'none' : 'auto',
      transform: `translateY(${(1 - textO) * -30}px)`,
      ...pos
    }
  }, /*#__PURE__*/React.createElement("h1", {
    className: "fadein-1",
    style: {
      color: P(c.text, '#fff'),
      textShadow: '0 2px 20px rgba(0,0,0,0.35)'
    },
    dangerouslySetInnerHTML: H(P(c.heading, 'More than fries.\nMore than food.'))
  }), /*#__PURE__*/React.createElement("p", {
    className: "fadein-2",
    style: {
      margin: 0,
      maxWidth: 640,
      fontFamily: 'var(--font-body)',
      fontWeight: 400,
      fontSize: 18,
      lineHeight: 1.4,
      color: P(c.text, '#fff'),
      textShadow: '0 1px 12px rgba(0,0,0,0.4)'
    },
    dangerouslySetInnerHTML: H(P(c.body, 'Restaurant-grade fries made for the moments that matter. Whether it’s a movie night, family dinner, game day, or unexpected guests, Cravy brings restaurant-quality fries to your table—so you can focus on what really matters: being together.'))
  }), /*#__PURE__*/React.createElement("div", {
    className: "fadein-3",
    style: {
      display: 'flex',
      gap: 14,
      flexWrap: 'wrap'
    }
  }, /*#__PURE__*/React.createElement("a", {
    className: "herobtn herobtn-dark",
    href: b1,
    style: {
      textDecoration: 'none',
      fontFamily: 'var(--font-body)',
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      padding: '14px 30px',
      background: P(c.btn1_bg, 'var(--cravy-yellow)'),
      color: P(c.btn1_color, '#000'),
      borderRadius: 999
    }
  }, P(c.btn1_text, 'Shop fries')), /*#__PURE__*/React.createElement("a", {
    className: "herobtn herobtn-lite",
    href: b2,
    style: {
      textDecoration: 'none',
      fontFamily: 'var(--font-body)',
      fontWeight: 700,
      fontSize: 16,
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      padding: '14px 30px',
      background: P(c.btn2_bg, 'rgba(255,255,255,0.95)'),
      color: P(c.btn2_color, '#000'),
      borderRadius: 999
    }
  }, P(c.btn2_text, 'Discover our story')))), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 26,
      display: 'flex',
      justifyContent: 'center',
      opacity: textO
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontFamily: 'var(--font-body)',
      fontWeight: 700,
      fontSize: 13,
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      color: '#fff',
      textShadow: '0 1px 10px rgba(0,0,0,0.6)',
      animation: 'bob 2.4s ease-in-out infinite'
    }
  }, "Scroll ▼"))));
}
window.CravyHome.hero = VideoHero;

// ---- PRODUCTS (live Shopify products, injected as cfg.products) ----
const ROTY = [{
  rot: -4,
  y: 0
}, {
  rot: 3,
  y: 40
}, {
  rot: -2,
  y: -30
}, {
  rot: 5,
  y: 20
}, {
  rot: -3,
  y: -15
}, {
  rot: 4,
  y: 30
}];
function Products({
  cfg
}) {
  const c = cfg || {};
  const flavors = (c.products || []).map((prod, i) => ({
    ...prod,
    rot: ROTY[i % ROTY.length].rot,
    y: ROTY[i % ROTY.length].y
  }));
  const [ref, p] = useScrollProgress();
  const rowRef = React.useRef(null);
  const [tx, setTx] = React.useState(-9999);
  React.useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const vw = innerWidth,
      rw = row.scrollWidth;
    setTx(-rw + p * (vw + rw));
  }, [p]);
  const text = P(c.text, '#FFFFFF');
  return /*#__PURE__*/React.createElement("section", {
    id: "products",
    ref: ref,
    "data-screen-label": "Product lineup",
    style: {
      height: '350vh',
      position: 'relative',
      background: P(c.bg, 'var(--cravy-orange)')
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'sticky',
      top: 0,
      height: '100vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    }
  }, P(c.logo, '') ? /*#__PURE__*/React.createElement("img", {
    src: c.logo,
    alt: "",
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      padding: '4vh 3vw',
      boxSizing: 'border-box',
      opacity: 0.13,
      pointerEvents: 'none'
    }
  }) : null, /*#__PURE__*/React.createElement("div", {
    style: {
      padding: '0 clamp(20px,5vw,80px)',
      position: 'absolute',
      top: 'clamp(40px,7vh,80px)',
      left: 0,
      right: 0,
      pointerEvents: 'none'
    }
  }, /*#__PURE__*/React.createElement("h2", {
    className: "fadein-1",
    style: {
      color: text,
      textAlign: 'left'
    },
    dangerouslySetInnerHTML: H(P(c.heading, 'Pick your pack'))
  }), /*#__PURE__*/React.createElement("p", {
    className: "fadein-2",
    style: {
      margin: '14px 0 0',
      maxWidth: 620,
      fontFamily: 'var(--font-body)',
      fontSize: 'clamp(15px,1.2vw,20px)',
      lineHeight: 1.2,
      color: text
    },
    dangerouslySetInnerHTML: H(P(c.body, 'Golden, crispy frozen fries made for every craving. Hover a pack.'))
  })), /*#__PURE__*/React.createElement("div", {
    ref: rowRef,
    style: {
      display: 'flex',
      gap: 'clamp(50px,7vw,130px)',
      width: 'max-content',
      alignItems: 'center',
      transform: `translateX(${tx}px)`,
      marginTop: '10vh'
    }
  }, flavors.map((f, i) => /*#__PURE__*/React.createElement(Pack, {
    key: f.name + i,
    f: f,
    i: i
  })))));
}
window.CravyHome.products = Products;
function Pack({
  f,
  i
}) {
  const [hov, setHov] = React.useState(false);
  return /*#__PURE__*/React.createElement("div", {
    onMouseEnter: () => setHov(true),
    onMouseLeave: () => setHov(false),
    style: {
      position: 'relative',
      width: 'clamp(220px,22vw,320px)',
      transform: `translateY(${f.y}px) rotate(calc(${f.rot}deg*var(--k,1)))`,
      cursor: 'pointer'
    }
  }, /*#__PURE__*/React.createElement("a", {
    href: f.url || '#',
    style: {
      display: 'block',
      width: '100%',
      aspectRatio: '1531/2164',
      transition: 'transform .35s cubic-bezier(.34,1.56,.64,1)',
      transform: hov ? 'scale(1.06) rotate(calc(-2deg*var(--k,1)))' : 'none',
      filter: 'drop-shadow(0 18px 30px rgba(0,0,0,0.28))'
    }
  }, /*#__PURE__*/React.createElement("image-slot", {
    id: 'pack-' + (i + 1),
    shape: "rect",
    fit: "contain",
    src: f.img,
    placeholder: 'Drop ' + f.name + ' packaging PNG'
  })), /*#__PURE__*/React.createElement("div", {
    style: {
      position: 'absolute',
      left: '50%',
      bottom: 'calc(100% - 8px)',
      transform: `translateX(-50%) scale(${hov ? 1 : 0.4})`,
      opacity: hov ? 1 : 0,
      transformOrigin: '50% 100%',
      transition: 'transform .3s cubic-bezier(.34,1.56,.64,1), opacity .2s',
      pointerEvents: hov ? 'auto' : 'none',
      zIndex: 5
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      background: '#fff',
      color: '#000',
      borderRadius: 18,
      padding: '18px 20px 16px',
      width: 250,
      boxShadow: '0 14px 34px rgba(0,0,0,0.28)',
      fontFamily: 'var(--font-body)',
      display: 'flex',
      flexDirection: 'column',
      gap: 6
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: {
      fontWeight: 700,
      fontSize: 17,
      lineHeight: 1.1
    }
  }, f.name), /*#__PURE__*/React.createElement("div", {
    style: {
      fontWeight: 400,
      fontSize: 12.5,
      color: '#555'
    }
  }, f.type), /*#__PURE__*/React.createElement("div", {
    style: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 16,
      marginTop: 8
    }
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: 700,
      fontSize: 17
    }
  }, f.price), /*#__PURE__*/React.createElement("span", {
    className: "addbtn",
    onClick: e => {
      e.preventDefault();
      e.stopPropagation();
      cravyAddToCart(f.vid);
    },
    style: {
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      padding: '9px 16px',
      background: '#000',
      color: '#fff',
      borderRadius: 999,
      cursor: 'pointer'
    }
  }, "Add to cart"))), /*#__PURE__*/React.createElement("div", {
    style: {
      width: 14,
      height: 14,
      background: '#fff',
      transform: 'rotate(45deg)',
      margin: '-8px auto 0',
      boxShadow: '6px 6px 12px rgba(0,0,0,0.12)'
    }
  })));
}
Object.assign(window, {
  VideoHero,
  Products,
  HomeMarquee,
  useIsMobile,
  cravyAddToCart,
  H,
  P
});

